/** @copyright (c) Viewpost. All Rights Reserved. See LICENSE for more details. */
/* eslint max-len:0 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import HeaderText from 'components/HeaderText';

import Tooltip from 'components/Tooltip';
import Icon, { IconTypes } from 'components/Icon';
import Button from 'components/Button';

import FormJSX from 'components/Form/FormJSX';
import FieldOption from 'components/Form/FieldOption';
import t from 'tcomb-validation';
import Messages from './index.messages';
import { MapStateToProps, MapDispatchToProps } from './index.connect';

import BeneficialOwnerCollectionForm from './Form';
import ExemptionForm from './ExemptionForm';

import './index.scss';

let mustBeTrue = t.subtype(t.Bool, val => val === true);

let beneficialCountSchema = t.struct({
  count: t.enums.of([0, 1, 2, 3, 4])
});

let confirmationSchema = t.struct({
  confirmed: mustBeTrue
});

class BeneficialOwnerCollectionPanel extends Component {

  constructor(props) {
    super(props);
    this.state = {
      beneficialOwnerFormState: null,
      confirmationFormState: null
    };
    this.beneficialOwnerFormRefs = [];
  }

  getMergedValue = () => {

    // since we're dealing with multiple subforms,
    // we're going to validate/merge them into a single model
    // we can use for the API call
    let mergedForm = {
      beneficialOwners: []
    };

    // validate Exemption subform
    mergedForm.exemption = this.exemptionForm.getValue();
    if (!mergedForm.exemption) {
      return null;
    }

    // validate Confirm subform
    let confirmFormValue = this.confirmationFormRef.getValue();
    if (!confirmFormValue) {
      return null;
    }

    if (mergedForm.exemption.exemptionType?.length
      && !this.requiresController(mergedForm.exemption.exemptionType)
      && !this.requiresOwners(mergedForm.exemption.exemptionType)) {
      // Exemption requires nothing more
      return mergedForm;
    }

    if (this.requiresController(mergedForm.exemption?.exemptionType)) {
      // validate controller form
      mergedForm.controllingOwner = this.controllingOwnerFormRef.getValue();
      if (!mergedForm.controllingOwner) {
        return null;
      }
    }

    if (this.requiresOwners(mergedForm.exemption?.exemptionType)) {
      // validate owners forms
      let beneficialCountValue = this.beneficialCountFormRef.getValue();
      if (!beneficialCountValue) {
        return null;
      }

      for (let x = 0; x < this.beneficialOwnerFormRefs.length; x++) {
        if (this.beneficialOwnerFormRefs[x]) {
          let value = this.beneficialOwnerFormRefs[x].getValue();
          if (!value) {
            return null;
          }

          if (value.copyFromOther) {
            mergedForm.beneficialOwners[x] = mergedForm.controllingOwner;
          } else {
            mergedForm.beneficialOwners[x] = value;
          }
        }
      }
    }

    if (mergedForm.exemption?.exemptionType === 'No Exemption') {
      // No need to actually save "no exemption"
      delete mergedForm.exemption;
    }

    return mergedForm;
  }

  requiresController(exemptionType) {
    return exemptionType === 'No Exemption'
      || exemptionType === 'Non-Profit Corporation or Charity'
      || exemptionType === 'Pooled investment vehicle that is operated or advised by a financial institution which is NOT excluded under paragraph (e)(2) of 31 CFR 1010.230';
  }

  requiresOwners(exemptionType) {
    return exemptionType === 'No Exemption';
  }

  submit = (onComplete) => {
    let formValue = this.getMergedValue();
    if (formValue) {
      this.props.onUpdate({
        ...formValue,
        secureToken: this.props.secureToken
      }, onComplete);
    }
  }

  renderControllingOwner() {
    if (!this.requiresController(this.state.exemptionValue?.exemptionType)) {
      return null;
    }
    return (
      <div className="form-section">
        <HeaderText>
          Individual with Control
        </HeaderText>
        <Messages.ControllingOwnerInstruction.Message />
        <BeneficialOwnerCollectionForm ref={ref => this.controllingOwnerFormRef = ref} />
      </div>
    );
  }

  renderBeneficialOwners() {
    if (!this.requiresOwners(this.state.exemptionValue?.exemptionType)) {
      return null;
    }
    return (
      <div>
        {this.renderBeneficialOwnerCount()}
        {this.renderBeneficialOwnerForms()}
      </div>
    );
  }

  renderBeneficialOwnerCount() {
    let tooltip = (
      <Tooltip overlay={<Messages.IndirectOwnershipTooltip.VpMessage />}>
        <Icon type={IconTypes.Info}/>
      </Tooltip>
    );

    return (
      <div className="form-section">
        <Messages.BeneficialOwnerCount.Message tooltip={tooltip}/> <br/><br/>
        <FormJSX ref={ref => this.beneficialCountFormRef = ref}
          modelType={beneficialCountSchema}
          value={this.state.beneficialCountFormState}
          onChange={val => this.setState({beneficialCountFormState: val})}>
          <FieldOption name="count" willHideLabel={true} />
        </FormJSX>
      </div>
    );
  }

  renderBeneficialOwnerForms() {
    this.beneficialOwnerFormRefs = [];

    const { count: originalCount } = this.state.beneficialCountFormState || {};
    const count = parseInt(originalCount || 0, 10);

    if (!count) {
      return <></>;
    }

    let forms = [];
    for (let x = 0; x < count; x++) {
      forms.push(
        <div className="form-section" key={x}>
          <HeaderText>
            Owner #{x + 1}
          </HeaderText>
          <BeneficialOwnerCollectionForm ref={ref => this.beneficialOwnerFormRefs[x] = ref}
            canCopyFromOther={x === 0} />
        </div>
      );
    }

    return (
      <div>
        <Messages.BeneficialOwnerInstruction.Message count={count} />
        {forms}
      </div>
    );
  }

  renderConfirmation() {
    return (
      <div className="form-section">
        <FormJSX ref={ref => this.confirmationFormRef = ref}
          modelType={confirmationSchema}
          value={this.state.confirmationFormState}
          onChange={val => this.setState({confirmationFormState: val})}>
          <FieldOption name="confirmed"
            label={Messages.CertifyStatement} />
        </FormJSX>
      </div>
    );
  }

  render() {
    return (
      <div className="component-beneficial-owner-collection-panel">
        <div style={{marginBottom: '20px'}}>
          <Messages.Instruction.VpMessage />
        </div>

        <ExemptionForm ref={ref => this.exemptionForm = ref}
          onChange={v => this.setState({ exemptionValue: v})} />

        {this.renderControllingOwner()}
        {this.renderBeneficialOwners()}

        {!!this.state.exemptionValue?.exemptionType
          ? (
            <div>
              {this.renderConfirmation()}
            </div>
          )
          : null}

        {this.props.showSubmit === false || !this.state.exemptionValue?.exemptionType
          ? null
          : (
            <Button onClick={() => this.submit(this.props.onComplete)}>
              Submit
            </Button>
          )}
      </div>
    );
  }
}

export default connect(MapStateToProps, MapDispatchToProps, null, { forwardRef: true })(
  BeneficialOwnerCollectionPanel
);
